import { IRPBodyContainerComponent } from '../../components/IRP/body/IRPBodyContainer'
import { EventCourseMetadata, EventCourses } from '../../utils/types'
import { useGetAzpEventEntryQuery, useGetIndividualResultQuery, useGetRaceAwardsQuery } from '../../api/reigniteApi'
import convertRaceTypeToTimeUnits from '../../utils/convertRaceTypeToTimeUnits'
import convertTimeUnitsToOldTimeUnit from '../../utils/convertTimeUnitsToOldTimeUnit'
import transformReigniteEntryIntervalPace from '../../utils/transformReigniteEntryIntervalPace'
import transformReigniteEntryIntervalTime from '../../utils/transformReigniteEntryIntervalTime'
import useSse from '../../lib/firebase/useSse'
import { ReigniteIndividualSseResult } from './ReigniteIndividualSseResult'
import useIrpMedia from '../../hooks/useIrpMedia'
import useFinisherCertificate from './useFinisherCertificate'
import findLastIndex from 'lodash/findLastIndex'
import { PaceUnits } from '../../types.results'
import { useEffect, useMemo, useState } from 'react'
import { IRPAward } from '../../components/IRP/body/IRPAwardSection'
import { IrpResult } from '../../types.results.reignite'

export function ReigniteIndividualResult(
  props: {
    athlinksEventId: number
    bib?: string
    courses: EventCourses
    entryId?: number
    event?: {
      ctliveId?: number
      timeZone: string
    }
    eventCourseId: number
    eventMetadata: any
    isLive: boolean
  }
) {
  const {
    athlinksEventId,
    bib,
    courses,
    eventCourseId,
    eventMetadata,
    entryId,
    isLive,
  } = props

  const usingSse = useSse(athlinksEventId) && isLive

  const {
    currentData: data,
    isFetching,
    isError,
    refetch,
  } = useGetIndividualResultQuery(
    {
      bib,
      entryId,
      eventId: athlinksEventId,
      eventCourseId: eventCourseId || 0,
    },
    {
      pollingInterval: isLive && !usingSse
        ? 30_000
        : undefined,
      skip: !eventCourseId || (!entryId && !bib),
    }
  )

  const { data: awardsData  } = useGetRaceAwardsQuery({
    eventCourseId,
    eventId: athlinksEventId,
  },
  {
    pollingInterval: isLive
      ? 30_000
      : undefined,
    skip: !eventCourseId || !athlinksEventId,
  })

  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState<IrpResult | null>(null)

  useEffect(() => {
    if (data && !isError) {
      setLastSuccessfulFetch(data)
    }
  }, [data, isError])


  const thirdPartyEntryId = lastSuccessfulFetch?.source !== 'mssql'
  ? +(lastSuccessfulFetch?.id ?? 0)
  : 0

  const athleteAwards = useMemo(() => {
    if (!awardsData || !thirdPartyEntryId) return []

    return awardsData.divisions?.reduce<IRPAward[]>((acc, division) => {
      const award = division.awards.find(
        (award) => award.id === thirdPartyEntryId
      )
      if (!award) return acc
      return [
        ...acc,
        {
          id: award.id,
          division: division,
          gender: award.gender,
          place: award.place,
        },
      ]
    }, []) ?? []
  }, [awardsData, thirdPartyEntryId])

  const media = useIrpMedia({
    thirdPartyEntryId,
    thirdPartyEventId: props?.event?.ctliveId ?? 0,
  })
  const certificate = useFinisherCertificate({
    athlinksEventId,
    bib,
    entryId,
    eventCourseId,
    thirdPartyEntryId,
  })

  const {
    data: azpEntry,
    isLoading: isAzpEntryLoading,
  } = useGetAzpEventEntryQuery({
    azp: 'ctlive',
    azpEntryId: thirdPartyEntryId ?? 0,
    azpEventId: eventMetadata?.data?.azpEventId,
  }, {
    skip: !eventMetadata?.data?.azpEventId || !thirdPartyEntryId,
  })

  const eventCourseMetadata: EventCourseMetadata = eventMetadata?.data?.eventCourseMetadata?.find((x: any) => x.eventCourseId === eventCourseId)
  const intervalDivisionMetaData = eventCourseMetadata?.metadata
  const metaIntervals = useMemo(() => intervalDivisionMetaData?.intervals ?? [], [intervalDivisionMetaData])
  const resultIntervals = useMemo(() => lastSuccessfulFetch?.intervals, [lastSuccessfulFetch])

  const raceType = eventCourseMetadata?.raceType ?? 'running'

  // Remove longest course interval which should be full-course and 
  // place last in array order since it usually has a displayOrder of 1 from CTlive.
  // This allows easier discovery of mostRecentInterval with result
  const longestMetaCourseInterval = useMemo(() => {
    return metaIntervals
      ?.filter(x => x.intervalType === 'course')
      ?.sort((a, b) => b.distance - a.distance)?.[0] ?? []
  }, [metaIntervals])

  // intervals from metadata are properly ordered and contain full list
  // enrich with result intervals we have so far
  const enrichedMetaIntervals = useMemo(() => {
    if (!metaIntervals?.length) return []
    return [
      ...metaIntervals.filter(x => x !== longestMetaCourseInterval),
      longestMetaCourseInterval
    ].filter(x => !!x).map((x) => {
      const resultInterval = resultIntervals?.find((y) => y.id === x.id)

      const metaInterval = {
        distance: {
          meters: x.distance,
          units: x.distanceUnit,
        },
        chipTimeInMillis: 0,
        divisions: [],
        gunTimeInMillis: 0,
        startTimeInMillis: 0,
        raceType: resultInterval?.raceType || 'run', // rename this to something like intervalRaceType or intervalActivityType in subsequent ticket
        name: x.name,
        id: x.id,
        paceUnits: 'none' as PaceUnits,
        full: x.isFull,
      }

      if (!resultInterval) {
        return metaInterval
      }

      return {
        ...metaInterval,
        ...resultInterval,
      }
    })
  }, [metaIntervals, longestMetaCourseInterval, resultIntervals])

  const isDataEmpty = (!lastSuccessfulFetch?.bib && !lastSuccessfulFetch?.id) || !enrichedMetaIntervals.length
  if (isDataEmpty) {
    return (
      <>
        <IRPBodyContainerComponent
          {...props}
          courses={courses}
          result={{
            fetching: isFetching,
          }}
          fetching={isFetching}
        />
      </>
    )
  }

  const {
    id,
    location,
    private: isPrivate, // 'private' is a reserved word in strict mode.
    race,
    source,
    displayName,
    ...pii
  } = lastSuccessfulFetch

  // full course interval isn't in metadata for unlinked triathalons, push from result intervals if exists
  if (raceType === 'triathlon & multisport' && !enrichedMetaIntervals.find((x) => x.full)) {
    const fullCourseIntervalResult = resultIntervals?.find((y) => y.full)
    if (!!fullCourseIntervalResult) {
      enrichedMetaIntervals.push(fullCourseIntervalResult)
    }
  }

  // find last interval with result
  const mostRecentIntervalIndex = findLastIndex(
    enrichedMetaIntervals,
    (x) => !!x.chipTimeInMillis || !!x.gunTimeInMillis
  )
  const firstInterval = lastSuccessfulFetch.intervals[0]
  const overallBracket = firstInterval?.divisions?.find(x => x.name?.toLowerCase() === 'overall')

  const publicDisplayName = displayName || 'Unknown Racer'
  const [firstName, lastName] = publicDisplayName.split(' ')

  const mapped = {
    ...pii,
    ...location,
    displayName: publicDisplayName,
    eventCourseId,
    eventId: athlinksEventId,
    id: source === 'mssql' ? id : 0,
    firstName,
    lastName,
    intervals: Array.from(enrichedMetaIntervals)
      .map(({
        chipTimeInMillis,
        divisions,
        gunTimeInMillis,
        startTimeInMillis,
        ...interval
      }, i) => {

        const overallDivision = divisions.find(x => x.name?.toLowerCase() === 'overall')

        const timeUnit = convertTimeUnitsToOldTimeUnit(
          convertRaceTypeToTimeUnits(interval.raceType)
        )
        return {
          ...interval,
          brackets: divisions.map(d => ({
            ...d,
            bracketName: d.name,
            bracketRankType: d.rankType,
            bracketType: d.type,
          })),
          chipTime: {
            timeInMillis: chipTimeInMillis,
            timeUnit,
          },
          gunTime: {
            timeInMillis: gunTimeInMillis,
            timeUnit,
          },
          intervalFull: interval.full || interval.name?.toUpperCase() === 'FULL COURSE', // TODO
          intervalName: interval.name,
          mostRecentInterval: i === mostRecentIntervalIndex,
          pace: !overallDivision ? 0 : transformReigniteEntryIntervalPace({
            chipTimeInMillis,
            division: overallDivision,
            gunTimeInMillis,
            interval,
          }),
          timeWithPenalties: !overallDivision ? 0 : transformReigniteEntryIntervalTime({
            chipTimeInMillis,
            gunTimeInMillis,
            raceType: interval.raceType,
            rankType: overallDivision.rankType,
          }),
        }
      }),
    isAzpEntryLoading,
    isProfilePublic: !isPrivate,
    racerStartTime: {
      timeInMillis: overallBracket?.rankType === 'gun'
        ? firstInterval?.startGunTimeInMillis || ((race.actualStartTime || race.plannedStartTime) * 1000)
        : firstInterval?.startTimeInMillis,
      timeZone: props.event?.timeZone,
    },
    ...(azpEntry?.teamRoster ? { teamRoster: azpEntry?.teamRoster } : {}),
    raceName: lastSuccessfulFetch.race.name,
    thirdPartyEntryId,
  }

  return (
    <>
      {usingSse && <ReigniteIndividualSseResult
        athlinksEventId={athlinksEventId}
        bib={bib}
        eventCourseId={eventCourseId}
        refetch={refetch}
      />}
      <IRPBodyContainerComponent
        {...props}
        awards={athleteAwards}
        certificate={certificate}
        courses={courses}
        result={mapped}
        fetching={isFetching}
        media={media}
      />
    </>
  )
}
